import type { DensityMode } from './appearance';
import type { CalendarView } from '@/features/calendar/types/calendar.types';

export type AppDensityMode = 'inherit' | DensityMode;

export type MatterViewMode = 'table' | 'card' | 'kanban';
export type DocumentViewMode = 'table' | 'grid';
export type ClientViewMode = 'table' | 'card' | 'kanban';

export type DocumentSortField = 'updated_at' | 'created_at' | 'title' | 'size' | 'type';
export type TaskSortField = 'due_date' | 'priority' | 'created_at';
export type TaskStatusFilter = 'all' | 'todo' | 'in_progress' | 'review' | 'done' | 'archived';
export type WeekStartOption = 'system' | 'sunday' | 'monday';
export type WorkingHoursMode = 'system' | 'custom';
export type LexAnswerStyle = 'direct' | 'issue_first' | 'narrative';
export type LexDetailLevel = 'concise' | 'balanced' | 'detailed';
export type LexCitationStrictness = 'source_first' | 'balanced' | 'minimal';

export interface MattersPreferences {
  defaultView: MatterViewMode;
  density: AppDensityMode;
  showKpiSummary: boolean;
}

export interface DocumentsPreferences {
  defaultView: DocumentViewMode;
  defaultSort: DocumentSortField;
  density: AppDensityMode;
  showAnalytics: boolean;
}

export interface ClientsPreferences {
  defaultView: ClientViewMode;
  density: AppDensityMode;
  showKpiSummary: boolean;
}

export interface TasksPreferences {
  defaultSort: TaskSortField;
  defaultStatusFilter: TaskStatusFilter;
  density: AppDensityMode;
  showOverdueHighlight: boolean;
}

export interface CalendarPreferences {
  defaultView: CalendarView;
  weekStart: WeekStartOption;
  showWeekends: boolean;
  workingHoursMode: WorkingHoursMode;
  workingHoursStart: string;
  workingHoursEnd: string;
  density: AppDensityMode;
}

export interface LexPreferences {
  personalization_enabled: boolean;
  allow_preference_memory_proposals: boolean;
  allow_cross_matter_context: boolean;
  require_confirmation_for_enriched_legal_facts: boolean;
  preferred_answer_style: LexAnswerStyle;
  default_detail_level: LexDetailLevel;
  citation_strictness: LexCitationStrictness;
  standing_instructions: string;
  /** Structured vocabulary hints for the intent classifier — phrase → what it
   * means in David's usage, with an optional governance task-type hint. The
   * only personalization channel that touches classification; a hint applies
   * only when its phrase appears in the message. */
  classifier_hints?: {
    phrases?: LexClassifierVocabularyHint[];
  };
}

export interface LexClassifierVocabularyHint {
  phrase: string;
  means: string;
  task_type?: string;
}

/**
 * EdgeMail preferences (`app_preferences.email`). Mirror of the backend
 * schema in services/edgemail/preferences/emailPreferences.ts: every key
 * changes real client behaviour (Settings completion plan, Wave 0).
 */
export interface EmailStartupPreferences {
  defaultFolder: 'inbox' | 'last';
  defaultView: 'last' | 'flat' | 'threaded';
  defaultTab: 'focused' | 'other';
  openReadingPane: boolean;
  restoreLastSession: boolean;
}
export interface EmailLayoutPreferences {
  readingPane: 'right' | 'bottom' | 'off';
  previewLines: 0 | 1;
  focusedOtherTabs: boolean;
  folderPaneMode: 'normal' | 'minimized' | 'hidden';
  rightRail: 'none' | 'lex' | 'notifications';
  dateGrouping: boolean;
  senderAvatars: boolean;
}
export interface EmailHandlingPreferences {
  markRead: 'on_open' | 'after_seconds' | 'manual';
  markReadSeconds: number;
  afterRemoval: 'next' | 'previous' | 'list';
}
export interface EmailComposePreferences {
  sendDelaySeconds: 0 | 5 | 10 | 30;
  attachmentReminder: boolean;
  externalRecipientWarning: boolean;
  bccSelf: boolean;
  defaultIdentityId: string | null;
}
export interface EmailPrivacyPreferences {
  remoteImages: 'always' | 'firm_and_matters' | 'ask' | 'never';
  trackerBlocking: boolean;
  externalLinkWarning: boolean;
  trustedSenders: string[];
}
export interface EmailJunkPreferences {
  suspiciousBanner: boolean;
}
export interface EmailSavedView {
  id: string;
  name: string;
  state: { filters: string[]; sender: string | null; domain: string | null; dateFrom?: string; dateTo?: string };
}
export interface EmailSearchPreferences {
  defaultScope: 'all' | 'mailbox';
  includeTrashJunk: boolean;
  savedViews: EmailSavedView[];
}
export interface EmailAttachmentsPreferences {
  defaultOpen: 'preview' | 'open' | 'download';
  linkAll: boolean;
  linkExpiryDays: number;
  warnUnknownSenders: boolean;
}
export interface EmailPeoplePreferences {
  pickerDefaultCategory: 'recent' | 'all' | 'clients' | 'colleagues' | 'counsel' | 'groups' | 'matters';
}
export interface EmailAccessibilityPreferences {
  keyboardScheme: 'both' | 'gmail' | 'outlook';
  reducedMotion: 'system' | 'on' | 'off';
  announceRows: boolean;
  focusRing: 'normal' | 'strong';
}
export interface EmailPreferences {
  version: 1;
  /** Kept at the top level for existing readers (Lex/voice defaults). */
  defaultMailboxId: string | null;
  startup: EmailStartupPreferences;
  layout: EmailLayoutPreferences;
  handling: EmailHandlingPreferences;
  compose: EmailComposePreferences;
  privacy: EmailPrivacyPreferences;
  junk: EmailJunkPreferences;
  search: EmailSearchPreferences;
  attachments: EmailAttachmentsPreferences;
  people: EmailPeoplePreferences;
  accessibility: EmailAccessibilityPreferences;
}
export type EmailPreferencesUpdate = {
  defaultMailboxId?: string | null;
  startup?: Partial<EmailStartupPreferences>;
  layout?: Partial<EmailLayoutPreferences>;
  handling?: Partial<EmailHandlingPreferences>;
  compose?: Partial<EmailComposePreferences>;
  privacy?: Partial<EmailPrivacyPreferences>;
  junk?: Partial<EmailJunkPreferences>;
  search?: Partial<EmailSearchPreferences>;
  attachments?: Partial<EmailAttachmentsPreferences>;
  people?: Partial<EmailPeoplePreferences>;
  accessibility?: Partial<EmailAccessibilityPreferences>;
};

export const DEFAULT_EMAIL_PREFERENCES: EmailPreferences = {
  version: 1,
  defaultMailboxId: null,
  startup: { defaultFolder: 'inbox', defaultView: 'last', defaultTab: 'focused', openReadingPane: true, restoreLastSession: true },
  layout: { readingPane: 'right', previewLines: 1, focusedOtherTabs: true, folderPaneMode: 'normal', rightRail: 'none', dateGrouping: true, senderAvatars: true },
  handling: { markRead: 'on_open', markReadSeconds: 3, afterRemoval: 'next' },
  compose: { sendDelaySeconds: 10, attachmentReminder: true, externalRecipientWarning: true, bccSelf: false, defaultIdentityId: null },
  privacy: { remoteImages: 'always', trackerBlocking: true, externalLinkWarning: false, trustedSenders: [] },
  junk: { suspiciousBanner: true },
  search: { defaultScope: 'all', includeTrashJunk: false, savedViews: [] },
  attachments: { defaultOpen: 'preview', linkAll: false, linkExpiryDays: 14, warnUnknownSenders: true },
  people: { pickerDefaultCategory: 'recent' },
  accessibility: { keyboardScheme: 'both', reducedMotion: 'system', announceRows: true, focusRing: 'normal' },
};

export function mergeEmailPreferences(base: EmailPreferences | undefined, update?: EmailPreferencesUpdate | Partial<EmailPreferences>): EmailPreferences {
  const current = base ?? DEFAULT_EMAIL_PREFERENCES;
  if (!update) return current;
  return {
    version: 1,
    defaultMailboxId: update.defaultMailboxId !== undefined ? update.defaultMailboxId : current.defaultMailboxId,
    startup: { ...current.startup, ...(update.startup || {}) },
    layout: { ...current.layout, ...(update.layout || {}) },
    handling: { ...current.handling, ...(update.handling || {}) },
    compose: { ...current.compose, ...(update.compose || {}) },
    privacy: { ...current.privacy, ...(update.privacy || {}) },
    junk: { ...current.junk, ...(update.junk || {}) },
    search: { ...current.search, ...(update.search || {}) },
    attachments: { ...current.attachments, ...(update.attachments || {}) },
    people: { ...current.people, ...(update.people || {}) },
    accessibility: { ...current.accessibility, ...(update.accessibility || {}) },
  };
}

export interface AppPreferences {
  matters: MattersPreferences;
  documents: DocumentsPreferences;
  clients: ClientsPreferences;
  tasks: TasksPreferences;
  calendar: CalendarPreferences;
  lex: LexPreferences;
  email: EmailPreferences;
}

export type AppPreferencesUpdate = {
  matters?: Partial<MattersPreferences>;
  documents?: Partial<DocumentsPreferences>;
  clients?: Partial<ClientsPreferences>;
  tasks?: Partial<TasksPreferences>;
  calendar?: Partial<CalendarPreferences>;
  lex?: Partial<LexPreferences>;
  email?: EmailPreferencesUpdate;
};

export const DEFAULT_APP_PREFERENCES: AppPreferences = {
  matters: {
    defaultView: 'table',
    density: 'inherit',
    showKpiSummary: true,
  },
  documents: {
    defaultView: 'table',
    defaultSort: 'updated_at',
    density: 'inherit',
    showAnalytics: true,
  },
  clients: {
    defaultView: 'table',
    density: 'inherit',
    showKpiSummary: true,
  },
  tasks: {
    defaultSort: 'due_date',
    defaultStatusFilter: 'all',
    density: 'inherit',
    showOverdueHighlight: true,
  },
  calendar: {
    defaultView: 'month',
    weekStart: 'system',
    showWeekends: true,
    workingHoursMode: 'system',
    workingHoursStart: '08:00',
    workingHoursEnd: '18:00',
    density: 'inherit',
  },
  lex: {
    personalization_enabled: true,
    allow_preference_memory_proposals: true,
    allow_cross_matter_context: false,
    require_confirmation_for_enriched_legal_facts: true,
    preferred_answer_style: 'direct',
    default_detail_level: 'balanced',
    citation_strictness: 'source_first',
    standing_instructions: '',
  },
  email: DEFAULT_EMAIL_PREFERENCES,
};
